import React, { forwardRef } from 'react';

import { Main, BasicFormWrapper } from '../../styled.js';
import { Button } from '../../../components/buttons/buttons.js';
import { Cards } from '../../../components/cards/frame/cards-frame.js';
import { AddUser } from '../style.js';
import Spinner from '../../common/Spinner.js';

import { useHostListingConfig } from './useHostListingConfig.js';
import ConfigView from './ConfigView.js';
import ConfigEdit from './ConfigEdit.js';

/**
 * ConfigAddEdit — orchestrator component for the host listing configuration tab.
 *
 * Three render states:
 *   1. No configuration yet  → empty state + "Add Default" button
 *   2. View mode             → <ConfigView /> (read-only)
 *   3. Edit mode             → <ConfigEdit /> (editable form)
 *
 * All state, effects, and handlers live in useHostListingConfig.
 * ManageSetting.js continues to use this component unchanged.
 */
const ConfigAddEdit = forwardRef((_props, _ref) => {
  const config = useHostListingConfig();

  const {
    spinnerLoader,
    hasConfiguration,
    isEditMode,
    setIsEditMode,

    // Props forwarded to ConfigView
    isMobileView,
    splitPanelHeight,
    screens,
    cardsBodyPadding,
    initialCardBodyPadding,
    viewCardBodyPadding,
    configuration,
    selectedTheme,
    fetchPermissionsbyHost,
  } = config;

  // ── 1. No configuration exists yet ─────────────────────────────────────
  if (!hasConfiguration && !isEditMode) {
    return (
      <>
        <Spinner loader={spinnerLoader} />
        <Cards xs={24} headless className="borderRadius common_fiter_table">
          <Main className="mainBlock">
            <AddUser>
              <BasicFormWrapper>
                <div className="listing-split-main">
                  <div className="centered-container">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: config.staticHelptext?.edit_action_setting_defaults,
                      }}
                    />
                  </div>
                </div>
              </BasicFormWrapper>
            </AddUser>
          </Main>
        </Cards>
        <div className="manage-listing-btn">
          <Button
            htmlType="button"
            outlined
            shape="circle"
            type="primary"
            key="add"
            className="w-100 edit-btn"
            onClick={() => setIsEditMode(true)}
          >
            Edit
          </Button>
        </div>
      </>
    );
  }

  // ── 2. View mode ────────────────────────────────────────────────────────
  if (hasConfiguration && !isEditMode) {
    return (
      <ConfigView
        spinnerLoader={spinnerLoader}
        isMobileView={isMobileView}
        splitPanelHeight={splitPanelHeight}
        screens={screens}
        initialCardBodyPadding={initialCardBodyPadding}
        viewCardBodyPadding={viewCardBodyPadding}
        configuration={configuration}
        selectedTheme={selectedTheme}
        fetchPermissionsbyHost={fetchPermissionsbyHost}
        onEdit={() => setIsEditMode(true)}
      />
    );
  }

  // ── 3. Edit mode ────────────────────────────────────────────────────────
  return (
    <ConfigEdit
      // Form
      form={config.form}
      // Refs
      quillEditorRef={config.quillEditorRef}
      // Layout
      screens={config.screens}
      cardsBodyPadding={config.cardsBodyPadding}
      initialCardBodyPadding={config.initialCardBodyPadding}
      isMobileView={config.isMobileView}
      splitPanelHeight={config.splitPanelHeight}
      // Constants
      maxCharCount={config.maxCharCount}
      hoursArray={config.hoursArray}
      daysBeforerules={config.daysBeforerules}
      isBlank={config.isBlank}
      // Spinner
      spinnerLoader={config.spinnerLoader}
      loading={config.loading}
      // Image
      previewImage={config.previewImage}
      showImageValidation={config.showImageValidation}
      listing_image={config.listing_image}
      // Time arrays
      unlockCheckInContentArray={config.unlockCheckInContentArray}
      unlockCheckInDoorCodeArray={config.unlockCheckInDoorCodeArray}
      lockCheckOutContentArray={config.lockCheckOutContentArray}
      lockCheckOutDoorCodeArray={config.lockCheckOutDoorCodeArray}
      displayArrivalCheckoutStatus={config.displayArrivalCheckoutStatus}
      maxDoorDays={config.maxDoorDays}
      doorcodeStatus={config.doorcodeStatus}
      // House rules
      house_rules={config.house_rules}
      isRequiredHouseRule={config.isRequiredHouseRule}
      maxLengthHouseRules={config.maxLengthHouseRules}
      // Rich text
      previewHouseManual={config.previewHouseManual}
      previewCheckoutInstructions={config.previewCheckoutInstructions}
      isModalVisible={config.isModalVisible}
      // Locals map
      addListingMap={config.addListingMap}
      isMapTitleRequired={config.isMapTitleRequired}
      isMapDescriptionRequired={config.isMapDescriptionRequired}
      isMapURLRequired={config.isMapURLRequired}
      isMapImageRequired={config.isMapImageRequired}
      // CMS
      previewCmsContent={config.previewCmsContent}
      openthemeCms={config.openthemeCms}
      // Form toggles
      useDefaultCheckTime={config.useDefaultCheckTime}
      useIndividualRecommendation={config.useIndividualRecommendation}
      useGlobalHouseManual={config.useGlobalHouseManual}
      useGlobalCheckoutInstructions={config.useGlobalCheckoutInstructions}
      selectedForm={config.selectedForm}
      userFormsDropList={config.userFormsDropList}
      configuration={config.configuration}
      // Theme
      listingThemeDropdownList={config.listingThemeDropdownList}
      selectedThemeSlug={config.selectedThemeSlug}
      // Setters
      setIsModalVisible={config.setIsModalVisible}
      setOpenthemeCms={config.setOpenthemeCms}
      setUseDefaultCheckTime={config.setUseDefaultCheckTime}
      setUseIndividualRecommendation={config.setUseIndividualRecommendation}
      setSelectedForm={config.setSelectedForm}
      // Handlers
      handleImageChange={config.handleImageChange}
      deletePreviewImage={config.deletePreviewImage}
      handleSelectCheckInTime={config.handleSelectCheckInTime}
      handleSelectCheckOutTime={config.handleSelectCheckOutTime}
      changeDisplayArrivalCheckoutInstructions={config.changeDisplayArrivalCheckoutInstructions}
      handleInputChange={config.handleInputChange}
      handleLinkClick={config.handleLinkClick}
      handleContentDaysChange={config.handleContentDaysChange}
      handleSelectContentCheckInTime={config.handleSelectContentCheckInTime}
      handleSelectContentCheckOutTime={config.handleSelectContentCheckOutTime}
      handleDoorCodeStatus={config.handleDoorCodeStatus}
      addHouseRule={config.addHouseRule}
      deleteHouseRule={config.deleteHouseRule}
      houseRulesChange={config.houseRulesChange}
      handleLocalsImageChanges={config.handleLocalsImageChanges}
      listingMapChange={config.listingMapChange}
      handleAddMapRow={config.handleAddMapRow}
      handleDeleteMapRow={config.handleDeleteMapRow}
      handleArrivalInstructionsValue={config.handleArrivalInstructionsValue}
      handlehouseManualValue={config.handlehouseManualValue}
      handleRentalInstructionsValue={config.handleRentalInstructionsValue}
      handleCheckoutInstructionsValue={config.handleCheckoutInstructionsValue}
      insertVariable={config.insertVariable}
      handleHostConfigSubmit={config.handleHostConfigSubmit}
      checkValidation={config.checkValidation}
      handleCancel={config.handleCancel}
      handleSelectTheme={config.handleSelectTheme}
      handleChangeFormsOptions={config.handleChangeFormsOptions}
      handleGlobalCheckoutChange={config.handleGlobalCheckoutChange}
      handleGlobalHouseManual={config.handleGlobalHouseManual}
      handleOpenModal={config.handleOpenModal}
      handleLocalMapCheckBox={config.handleLocalMapCheckBox}
      staticHelptext={config.staticHelptext}
      fetchPermissionsbyHost={fetchPermissionsbyHost}
      welcomVideoModal={config.welcomVideoModal}
      earlyCheckInActivePopup={config.earlyCheckInActivePopup}
      setEarlyCheckInActivePopup={config.setEarlyCheckInActivePopup}
      showEarlyCheckingInfo = {config.showEarlyCheckingInfo}
      earlyCheckinPopupInfo = {config.earlyCheckinPopupInfo}
      setEarlyCheckinPopupInfo = {config.setEarlyCheckinPopupInfo}
      themeInfoModal = {config.themeInfoModal}
      setThemeInfoModal = {config.setThemeInfoModal}
      state={config.state}  
      setState={config.setState}
      closeEarlyCheckInPopupInfo={config.closeEarlyCheckInPopupInfo}
      jotformActivePopup={config.jotformActivePopup}
      setJotformActivePopup={config.setJotformActivePopup}
      editorHouseManualRef={config.editorHouseManualRef}
      editorCheckoutInstructionRef={config.editorCheckoutInstructionRef}
    />
  );
});

export default ConfigAddEdit;
